import React, { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme, ThemeMode } from '@/lib/theme';

const OPTIONS: Array<{ mode: ThemeMode; label: string; icon: React.ReactNode }> = [
  { mode: 'light', label: 'Light', icon: <Sun size={16} /> },
  { mode: 'dark', label: 'Dark', icon: <Moon size={16} /> },
  { mode: 'system', label: 'System', icon: <Monitor size={16} /> },
];

// Compact icon popover — used in the top header.
export const ThemeToggle: React.FC = () => {
  const { mode, resolved, setMode } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const Icon = resolved === 'dark' ? Moon : Sun;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <Button variant="ghost" size="icon" data-testid="theme-toggle" aria-label="Change theme" onClick={() => setOpen((o) => !o)}>
        <Icon size={18} />
      </Button>
      {open && (
        <div className="absolute right-0 mt-2 w-40 rounded-md border border-border bg-popover text-popover-foreground shadow-lg p-1 z-50 animate-fade-in">
          {OPTIONS.map((o) => (
            <button
              key={o.mode}
              type="button"
              data-testid={`theme-option-${o.mode}`}
              onClick={() => { setMode(o.mode); setOpen(false); }}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              {o.icon}
              <span className="flex-1 text-left">{o.label}</span>
              {mode === o.mode && <Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Explicit segmented control — used on Profile & Settings.
export const ThemeSegmented: React.FC = () => {
  const { mode, setMode } = useTheme();
  return (
    <div className="inline-flex rounded-lg border border-border p-1 bg-muted/40" data-testid="theme-segmented">
      {OPTIONS.map((o) => {
        const active = mode === o.mode;
        return (
          <button
            key={o.mode}
            type="button"
            data-testid={`theme-seg-${o.mode}`}
            onClick={() => setMode(o.mode)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-body transition-colors ${
              active ? 'bg-background shadow-sm text-foreground font-semibold' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {o.icon}
            <span>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
};
